import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { dynamicElementsInputWithIconKey } from '@epicuro-next/constants/dynamic-elements';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { IconPathData } from '@fortawesome/fontawesome-common-types';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { BehaviorSubject, interval, merge, Subject } from 'rxjs';
import {
  debounce,
  distinctUntilChanged,
  filter,
  map,
  scan,
} from 'rxjs/operators';

import { IconPlacement } from './model';

/**
 * Input with icon Component
 */
@DynamicComponentRegister(dynamicElementsInputWithIconKey, true)
@Component({
  selector: 'atx-input-with-icon',
  templateUrl: './input-with-icon.component.html',
  styleUrls: ['./input-with-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputWithIconComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class InputWithIconComponent
  implements OnChanges, OnDestroy, ControlValueAccessor
{
  /**
   * The initial value for the input
   */
  @Input() public initialValue: string;

  /**
   * The string to display as a placeholder
   */
  @Input() public placeholderText: string;

  /**
   * The flag to trigger update without delay
   * Supported focusout, enter
   */
  @Input() public updateOnEvents = false;

  /**
   * The flag for ignoring current input value
   * stops trigger update when true and new value starts with previous
   * ex. Can be used when input is used as search query and current value has no search result
   */
  @Input() public ignoreConsequentInputs = false;

  /**
   * Disabled state
   */
  private isDisabled = false;

  @Input()
  public get isInputDisabled() {
    return this.isDisabled;
  }

  public set isInputDisabled(value: boolean) {
    this.isDisabled = coerceBooleanProperty(value);
    const action = this.isDisabled ? 'disable' : 'enable';
    this.inputControl[action]({ emitEvent: false });
  }

  /**
   * Icon to display
   */
  @Input() public icon: IconDefinition | IconPathData;

  /**
   * Where to position the icon
   */
  @Input() public iconPlacement: IconPlacement = 'left';

  /**
   * Icon and text size to display
   */
  @Input() public size: 'sm' | 'lg' = 'sm';

  @Input() public maxLength: number;

  /**
   * Icon replaced with a spinner when loading is true
   */
  @Input() public loading = false;

  /**
   * Debounce time before value change event emitted
   */
  @Input() public debounceTime = 500;

  /**
   * Event that emits the value when it changes
   */
  @Output() public valueChange = new EventEmitter<string>();

  // fix to get value for Dynamic Item as valueChange skips the first update
  @Output() public currentValueSub = new BehaviorSubject<string>('');

  @Output()
  public onfocus: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostBinding('class.sm')
  public get isMedium(): boolean {
    return this.size === 'sm';
  }

  @HostBinding('class.lg')
  public get isLarge(): boolean {
    return this.size === 'lg';
  }

  /**
   * Input form control
   */
  public inputControl = new UntypedFormControl('');

  private updateOnEvent$$ = new Subject<void>();

  /**
   * Listens for value changes and emits them after provided delay
   */
  private onDelayUpdate$ = this.inputControl.valueChanges.pipe(
    distinctUntilChanged(),
    scan(
      ([previousSearch]: [string], currentSearch: string) => {
        let debounceTime = 0;
        // update with delay from input when typing
        // otherwise update without delay when paste value or clear
        if (
          currentSearch &&
          Math.abs(currentSearch.length - (previousSearch ?? '').length) === 1
        ) {
          debounceTime = this.debounceTime;
        }

        return [currentSearch, debounceTime];
      },
      ['', 0],
    ),
    debounce(([_, debounceTime]: [string, number]) => interval(debounceTime)),
  );

  private updateValue$ = merge(this.onDelayUpdate$, this.updateOnEvent$$).pipe(
    filter(() => this.inputControl.dirty),
    map(() => this.inputControl.value),
    distinctUntilChanged(),
    scan(
      ([previous]: [string], current: string) => {
        const ignored =
          current?.startsWith(previous) && this.ignoreConsequentInputs;
        return ignored ? [previous, ignored] : [current, ignored];
      },
      [null],
    ),
    filter(([, ignored]) => !ignored),
  );

  private changesSub = this.updateValue$.subscribe(() => {
    this.currentValueSub.next(this.inputControl.value);
    this.valueChange.emit(this.inputControl.value);
    this.onChange(this.inputControl.value);
    this.onTouch(this.inputControl.value);
  });

  @HostListener('focusout')
  @HostListener('keyup.enter')
  public handleUpdateEvents() {
    if (this.updateOnEvents) {
      this.updateOnEvent$$.next();
    }
  }

  /**
   * Updates the state of the component every time the inputs change.
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.initialValue &&
      changes.initialValue.currentValue !== changes.initialValue.previousValue
    ) {
      this.inputControl.setValue(changes.initialValue.currentValue, {
        emitEvent: false,
      });
    }
  }

  /**
   * Function to trigger onChange
   */
  public onChange: (arg: string) => void = () => {};

  /**
   * Function to trigger onTouched
   */
  public onTouch: (arg: string) => void = () => {};

  /**
   * Sets the value of the input
   * @param value Value to set
   */
  public writeValue(value: string): void {
    this.inputControl.setValue(value, {
      // emit event to update prev value on onDelayUpdate$ stream when clear button is clicked
      emitEvent: !value && value !== this.inputControl.value,
    });
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onChange
   */
  public registerOnChange(fn: (arg: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onTouched
   */
  public registerOnTouched(fn: (arg: string) => void): void {
    this.onTouch = fn;
  }

  /**
   * Subscription cleanup
   */
  public ngOnDestroy(): void {
    this.changesSub.unsubscribe();
  }

  public onFocus(focus: boolean) {
    this.onfocus.emit(focus);
  }
}
